"use client"

import { ThinkingOrb } from "thinking-orbs"
import type { OrbSize, OrbState, OrbTheme } from "thinking-orbs"
import type { CSSProperties } from "react"

const atlasOrbTint =
  "bg-transparent opacity-100 [filter:invert(55%)_sepia(98%)_saturate(3000%)_hue-rotate(348deg)_brightness(112%)_contrast(140%)_drop-shadow(0_0_8px_rgb(255_115_23/0.74))] dark:[filter:invert(61%)_sepia(46%)_saturate(980%)_hue-rotate(111deg)_brightness(106%)_contrast(126%)_drop-shadow(0_0_8px_rgb(84_179_147/0.78))]"
const atlasSuccessOrbTint =
  "bg-transparent opacity-100 [filter:invert(61%)_sepia(46%)_saturate(980%)_hue-rotate(111deg)_brightness(106%)_contrast(126%)_drop-shadow(0_0_8px_rgb(84_179_147/0.78))]"

type AtlasThinkingOrbProps = {
  state: OrbState
  size?: OrbSize
  speed?: number
  theme?: OrbTheme
  className?: string
  style?: CSSProperties
  label?: string
}

export default function AtlasThinkingOrb({
  state,
  size = 20,
  speed = 1,
  theme = "auto",
  className,
  style,
  label = "Atlas thinking",
}: AtlasThinkingOrbProps) {
  return (
    <ThinkingOrb
      state={state}
      size={size}
      speed={speed}
      theme={theme}
      aria-label={label}
      className={className}
      style={style}
    />
  )
}

export function AtlasInlineOrb({
  state = "breathing",
  tone = "auto",
  className = "",
}: {
  state?: OrbState
  tone?: "auto" | "success"
  className?: string
}) {
  const orbStyle: CSSProperties = { height: 17, width: 17 }
  const tintClassName = tone === "success" ? atlasSuccessOrbTint : atlasOrbTint

  return (
    <span
      aria-hidden="true"
      className={`mx-[0.625rem] inline-flex h-[1em] w-[17px] translate-y-[-0.08em] items-center justify-center bg-transparent align-middle leading-none ${className}`}
    >
      <span className="relative block size-[17px] bg-transparent leading-none">
        <AtlasThinkingOrb
          state={state}
          size={20}
          speed={0.75}
          theme="light"
          className={`absolute inset-0 size-[17px] ${tintClassName}`}
          style={orbStyle}
          label="Atlas"
        />
        <AtlasThinkingOrb
          state={state}
          size={20}
          speed={0.75}
          theme="light"
          className={`absolute inset-0 size-[17px] opacity-65 ${tintClassName}`}
          style={orbStyle}
          label="Atlas"
        />
      </span>
    </span>
  )
}

export function AtlasNodeOrb({
  state = "working",
  className = "",
}: {
  state?: OrbState
  className?: string
}) {
  const orbStyle: CSSProperties = { height: 34, width: 34 }

  return (
    <span
      aria-hidden="true"
      className={`relative flex size-10 shrink-0 items-center justify-center rounded-full border border-brand-500/20 bg-white/65 shadow-[0_0_24px_rgb(255_115_23/0.22)] backdrop-blur-sm dark:border-jungle-400/25 dark:bg-white/[0.045] dark:shadow-[0_0_24px_rgb(84_179_147/0.24)] ${className}`}
    >
      <AtlasThinkingOrb
        state={state}
        size={64}
        speed={0.68}
        theme="light"
        className={`size-[34px] ${atlasOrbTint}`}
        style={orbStyle}
        label="Atlas processing"
      />
    </span>
  )
}
